import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { Rocket, ArrowRight, Loader2 } from "lucide-react";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Tilt3D } from "../components/Tilt3D";
import { FloatingShapes } from "../components/FloatingShapes";
import { PasswordInput } from "../components/PasswordInput";

export default function Login({ onSwitch }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const heroRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(".login-reveal", { opacity: 0, y: 30, rotateX: 18, transformPerspective: 900 }, { opacity: 1, y: 0, rotateX: 0, duration: 0.9, stagger: 0.1, ease: "power3.out", clearProps: "transform" });
      gsap.to(".brand-cube", { rotateY: 18, rotateX: -10, duration: 2.4, repeat: -1, yoyo: true, ease: "sine.inOut", transformPerspective: 400 });
    }, heroRef);
    return () => ctx.revert();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.trim().toLowerCase(), password });
      login(data.access_token, data.user);
    } catch (err) {
      setError(errorMessage(err, "Email o password non corretti"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div ref={heroRef} className="relative flex min-h-screen items-center overflow-hidden p-6">
      <FloatingShapes dense />
      <div className="persp relative z-10 mx-auto w-full max-w-md md:ml-[12vw]">
        <div className="login-reveal mb-8 flex items-center gap-3">
          <div className="brand-cube flex h-11 w-11 items-center justify-center rounded-xl bg-primary shadow-[0_0_32px_rgba(37,99,235,0.5)]" style={{ transformStyle: "preserve-3d" }}>
            <Rocket className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-xl font-bold">Sales<span className="text-primary">Pailot</span></p>
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Area riservata</p>
          </div>
        </div>
        <h1 className="login-reveal text-3xl font-bold tracking-tight sm:text-4xl">Accedi al tuo spazio</h1>
        <p className="login-reveal mt-3 text-sm text-muted-foreground">Ogni venditore ha i propri contenuti, le proprie call e la propria voce.</p>
        <Tilt3D max={6} className="login-reveal mt-8 rounded-xl">
          <form onSubmit={submit} className="surface space-y-4 p-6">
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground" htmlFor="email">Email</label>
              <input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                required
                className="w-full rounded-lg border border-input bg-background px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
                placeholder="nome@azienda.com"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground" htmlFor="password">Password</label>
              <PasswordInput
                id="password"
                testId="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••••"
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm text-red-400">{error}</p>
            )}
            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading || !email || !password}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-white transition-all hover:bg-[#1D4ED8] disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 spin-slow" /> : <>Entra <ArrowRight className="h-4 w-4" /></>}
            </button>
            <p className="pt-1 text-center text-sm text-muted-foreground">
              Non hai un account?{" "}
              <button type="button" data-testid="go-register-btn" onClick={onSwitch} className="font-semibold text-primary hover:underline">
                Registrati
              </button>
            </p>
          </form>
        </Tilt3D>
      </div>
    </div>
  );
}
